import { SpaceBetween, Icon, Button, Header, Alert, Link } from '@amzn/awsui-components-react';
import { IneligibilityReasons } from '@amzn/red-velvet-api';
import { useTranslation } from 'react-i18next';
import { Auth } from 'aws-amplify';
import { useEffect, useState } from 'react';

interface AmbassadorRegistrationFailureProps {
  ineligibilityReasons: IneligibilityReasons | undefined;
}

export const AmbassadorRegistrationSuccess = () => {
  const { t } = useTranslation(['ambassadors', 'translation']);

  return (
    <SpaceBetween direction="vertical" size="s" alignItems="center">
      <Icon name="status-positive" size="large" variant="success" />
      <Header variant="h1">{t('ambassadorRegistration.result.success.successTitle')}</Header>
      <p>{t('ambassadorRegistration.result.success.successDetail')}</p>
      <Alert
        statusIconAriaLabel="Info"
        header={t('ambassadorRegistration.result.success.ambassadorsInYourArea', { count: 9 })}
      >
        <SpaceBetween direction="vertical" size="xxxs">
          {t('ambassadorRegistration.result.success.teamUp')}
          {/* This link will be updated in: https://sim.amazon.com/issues/cce-6329 */}
          <Link
            href="https://t.corp.amazon.com/create/templates/9cedc919-f013-43b0-9e96-e096bd8bbb5a"
            target="_blank"
          >
            {t('ambassadorRegistration.result.success.viewAmbassadors')}
          </Link>
        </SpaceBetween>
      </Alert>
      <div style={{ justifyItems: 'center' }}>
        <SpaceBetween direction="horizontal" size="s">
          <Button variant="normal">{t('ambassadorRegistration.result.success.createEvent')}</Button>
          <Button data-ambassador-registration-go-to-profile-button>
            {t('ambassadorRegistration.result.success.goToProfile')}
          </Button>
        </SpaceBetween>
      </div>
    </SpaceBetween>
  );
};

export const AmbassadorRegistrationFailure = ({
  ineligibilityReasons,
}: AmbassadorRegistrationFailureProps) => {
  const { t } = useTranslation(['ambassadors', 'translation']);
  const [sixMonthsDate, setSixMonthsDate] = useState<string>(
    t('ambassadorRegistration.result.error.sixMonthsFromTheDate')
  );

  useEffect(() => {
    const fetchDate = async () => {
      try {
        const session = await Auth.currentSession();
        const idToken = session.getIdToken();
        const dateCreated = idToken.payload['identities']?.[0]?.dateCreated;

        if (dateCreated) {
          const updatedDate = new Date(parseInt(dateCreated));
          updatedDate.setMonth(updatedDate.getMonth() + 6);
          const formattedDate = updatedDate.toLocaleString('en-US', {
            year: 'numeric',
            month: 'long',
          });
          setSixMonthsDate(formattedDate);
        }
      } catch (error) {
        console.warn(error);
      }
    };
    fetchDate();
  }, [t]);

  const alertIneligibilityReasons = () => {
    const alertValues = {
      employmentStatus: {
        title: t('ambassadorRegistration.result.error.employmentStatus'),
        action: t('ambassadorRegistration.result.error.submitOnceYouveMetEmploymentStatus'),
      },
      badgeColor: {
        title: t('ambassadorRegistration.result.error.badgeColor'),
        action: t('ambassadorRegistration.result.error.youNeedBlueBadge'),
      },
      jobLevel: {
        title: t('ambassadorRegistration.result.error.jobLevel'),
        action: t('ambassadorRegistration.result.error.jobLevelNotHighEnough'),
      },
      tenure: {
        title: t('ambassadorRegistration.result.error.reapplyIn', {
          reapplyDate: sixMonthsDate,
        }),
        action: t('ambassadorRegistration.result.error.submitOnceYouveMetTenure'),
      },
    };

    if (!ineligibilityReasons) return null;

    return Object.keys(ineligibilityReasons as object).map((key, index) => {
      return (
        <li key={index}>
          <b>{alertValues[key as keyof IneligibilityReasons]?.title}</b> -{' '}
          {alertValues[key as keyof IneligibilityReasons]?.action}
        </li>
      );
    });
  };

  const whatYouCanDoListItems = () => {
    const listItemValues = {
      employmentStatus: {
        title: t('ambassadorRegistration.result.error.employmentStatus'),
        action: t('ambassadorRegistration.result.error.submitOnceYouveMetEmploymentStatus'),
      },
      badgeColor: {
        title: t('ambassadorRegistration.result.error.badgeColor'),
        action: t('ambassadorRegistration.result.error.submitOnceBlueBadgeAndManagerApproves'),
      },
      jobLevel: {
        title: t('ambassadorRegistration.result.error.jobLevel'),
        action: t('ambassadorRegistration.result.error.submitOnceL3AndManagerApproves'),
      },
      tenure: {
        title: t('ambassadorRegistration.result.error.reapplyIn', {
          reapplyDate: sixMonthsDate,
        }),
        action: t('ambassadorRegistration.result.error.submitOnceTenureAndManagerApproves'),
      },
    };

    if (!ineligibilityReasons) return null;

    return Object.keys(ineligibilityReasons as object).map((key, index) => {
      return (
        <li key={index}>
          <b>{listItemValues[key as keyof IneligibilityReasons]?.title}</b>
          <br />
          {listItemValues[key as keyof IneligibilityReasons]?.action}
        </li>
      );
    });
  };

  return (
    <SpaceBetween direction="vertical" size="s" data-ambassador-registration-failure-container>
      <SpaceBetween direction="vertical" size="s" alignItems="center">
        <Icon name="status-warning" size="large" variant="error" />
        <Header variant="h1">{t('ambassadorRegistration.result.error.errorTitle')}</Header>
        <p>{t('ambassadorRegistration.result.error.errorDetail')}</p>
        <Alert
          data-testId="ambassador-registration-failure-alert"
          statusIconAriaLabel="Warning"
          type="warning"
          header={t('ambassadorRegistration.result.error.followingReasons')}
        >
          <SpaceBetween direction="vertical" size="xxxs">
            <ul style={{ paddingLeft: 0 }}>{alertIneligibilityReasons()}</ul>
          </SpaceBetween>
        </Alert>
        <p>
          {t('ambassadorRegistration.result.error.ifYouBelieve')}
          <Link
            href="https://t.corp.amazon.com/create/templates/9cedc919-f013-43b0-9e96-e096bd8bbb5a"
            target="_blank"
          >
            {t('ambassadorRegistration.result.error.supportTool')}
          </Link>
        </p>
      </SpaceBetween>
      <div style={{ width: '100%' }}>
        <Header variant="h3">{t('ambassadorRegistration.result.error.whatYouCanDo')}</Header>
        <ol style={{ paddingLeft: 0, listStyle: 'outside inside', padding: '0 16px' }}>
          {whatYouCanDoListItems()}
        </ol>
      </div>
    </SpaceBetween>
  );
};
